/* eslint-disable react-hooks/rules-of-hooks */
import React, { useEffect, useState, useRef } from 'react';
import { Form, Input, Checkbox, Button, Select, Upload, Radio, Switch } from 'antd';
import { InboxOutlined, UploadOutlined, LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { SketchPicker } from 'react-color';
import { isUrl } from 'src/utils/helpers';
import './index.css';
const { TextArea } = Input;
const { Option } = Select;

const CustomForm = ({ form, fields, initialValues = {}, ...props }) => {

  // by default, antd won't use defaultValue of input field for its form, so we have to use initialValues manually extracted from defaultValue of each field
  const getInitialValues = () => {
    let initialValuesObj = {};
    fields.forEach(field => {
      if (field?.inputProps?.hasOwnProperty('defaultValue')) {
        initialValuesObj[field.name] = field.inputProps.defaultValue;
      }
    });
    return initialValuesObj;
  };

  const Field = ({ children, label, name, rules, ...field }) => (
    <Form.Item
      label={label}
      name={name}
      rules={rules}
      {...field}
    >
      {children}
    </Form.Item>
  );

  return (
    <Form
      form={form}
      initialValues={{ ...getInitialValues(), ...initialValues }}
      {...props}
    >
      {fields.map((field) => {
        const { type, label, name, inputProps = {} } = field;
        switch (type) {
          case 'text':
            return (
              <Field {...field} key={name}>
                <Input {...inputProps} />
              </Field>
            );
          case 'textarea':
            return (
              <Field {...field} key={name}>
                <TextArea
                  showCount
                  style={{ height: 100 }}
                  {...inputProps}
                />
              </Field>
            );
          case 'checkbox': {
            const { title, defaultValue, ...remainingInputProps } = inputProps;
            return (
              <Field {...field} key={name} valuePropName="checked">
                <Checkbox {...remainingInputProps} defaultChecked={defaultValue}>{title}</Checkbox>
              </Field>
            );
          }
          case 'select':
            return (
              <Field {...field} key={name}>
                <Select {...inputProps} />
              </Field>
            );
          case 'radio':
            return (
              <Field {...field} key={name}>
                <Radio.Group {...inputProps} />
              </Field>
            );
          case 'switch': {
            const { defaultValue, ...remainingInputProps } = inputProps;
            const checked = Form.useWatch(name, form);

            const onChange = (value) => {
              form.setFieldValue(name, value);
            };

            return (
              <>
                <Field {...field} key={name} valuePropName="checked" hidden />
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
                  <span style={{ marginRight: 10 }}>{label}</span>
                  <Switch
                    defaultChecked={defaultValue}
                    checked={checked}
                    onChange={onChange}
                    {...remainingInputProps}
                  />
                </div>
              </>
            );
          }
          case 'button': {
            const { title, align, ...remainingInputProps } = inputProps;
            return (
              <div style={{ display: 'flex', justifyContent: align }}>
                <Button {...remainingInputProps}>
                  {title}
                </Button>
              </div>
            );
          }
          case 'image': {
            const { directory, multiple } = inputProps;
            const [loading, setLoading] = useState(false);
            const [imageUrl, setImageUrl] = useState(null);
            const image = Form.useWatch(name, form);

            useEffect(() => {
              (isUrl(image) || !image) && setImageUrl(image);
            }, [image]);

            const getBase64 = (img, callback) => {
              const reader = new FileReader();
              reader.addEventListener('load', () => callback(reader.result));
              reader.readAsDataURL(img);
            };

            const normFile = (e) => {
              console.log('Upload event:', e);

              if (Array.isArray(e)) {
                return e;
              }

              return e?.fileList;
            };

            const handleChange = ({ file, fileList }) => {
              if (fileList[0]?.originFileObj) {
                setLoading(true);
                getBase64(fileList[0].originFileObj, (url) => {
                  setLoading(false);
                  setImageUrl(url);
                });
              }
            };

            const uploadButton = (
              <div>
                {loading ? <LoadingOutlined /> : <PlusOutlined />}
                <div style={{ marginTop: 8 }}>
                  Upload
                </div>
              </div>
            );

            return (
              <Field
                {...field}
                key={name}
                valuePropName={Array.isArray(image) ? 'fileList' : undefined} // only use this prop if value is a array of file objects
                getValueFromEvent={normFile}
              >
                <Upload
                  name={name}
                  listType="picture-card"
                  beforeUpload={() => false}
                  showUploadList={directory || multiple ? true : false}
                  maxCount={directory || multiple ? undefined : 1}
                  onChange={handleChange}
                  {...inputProps}
                >
                  {imageUrl && !directory && !multiple ? (
                    <img
                      src={imageUrl}
                      alt={name}
                      style={{
                        width: '100%',
                        height: 200,
                        objectFit: 'contain'
                      }}
                    />
                  ) : (
                    uploadButton
                  )}
                </Upload>
              </Field>
            );
          }
          case 'colorPicker': {
            const { alwaysShowPicker } = inputProps;
            const [pickerVisible, setPickerVisible] = useState(false);
            const [color, setColor] = useState('#fff');
            const value = Form.useWatch(name, form);

            useEffect(() => {
              setColor(value);
            }, [value]);

            const handleChange = (color) => {
              setColor(color.hex);
              form.setFieldValue(name, color.hex);
            };

            return (
              <div key={name} onClick={() => pickerVisible && setPickerVisible(false)}>
                {/* wrapping react-color component inside Field causes unnecessary re-render */}
                <Field {...field} key={name} hidden />
                <div style={{ display: 'flex', marginBottom: 20, alignItems: 'center' }} onClick={() => setPickerVisible(true)}>
                  {label}
                  <div style={{
                    width: 60,
                    height: 30,
                    borderRadius: 2,
                    padding: 5,
                    boxShadow: '0 0 0 1px rgba(0,0,0,.1)',
                    cursor: 'pointer',
                    marginLeft: 20
                  }}>
                    <div style={{ width: '100%', height: '100%', backgroundColor: color }} />
                  </div>
                </div>
                {alwaysShowPicker ?
                  <SketchPicker
                    color={color}
                    onChange={handleChange}
                    {...inputProps}
                  />
                  : (
                    pickerVisible && <SketchPicker
                      color={color}
                      onChange={handleChange}
                      {...inputProps}
                    />
                  )
                }
              </div>
            );
          }
          default:
            return null;
        }
      })}
    </Form>
  )
}

export default CustomForm;